import { useEffect, useState } from "react"
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material"
import { useParams } from "react-router-dom"
import { doc, getDoc, updateDoc } from "firebase/firestore"
import dayjs from "dayjs"
import { db } from "../firebase"
import { toast } from 'react-toastify'
import { useAuthState } from "../components/hooks/useAuthState"
import { HEALTH_CHECK_ITEMS } from "../utils/healthCheckItems"

interface HealthCheckData {
  id?: string
  employeeId: string
  employeeName: string
  email: string
  date: string
  status: string
  checks?: Record<string, string>
  remarks?: string
}

const HealthCheckRespond = () => {
  const { id } = useParams<{ id: string }>()
  const { email, isLoading: authLoading } = useAuthState()

  const [loading, setLoading] = useState<boolean>(true)
  const [processing, setProcessing] = useState<boolean>(false)
  const [record, setRecord] = useState<HealthCheckData | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [checks, setChecks] = useState<Record<string, string>>({})
  const [remarks, setRemarks] = useState<string>("")
  const [submitted, setSubmitted] = useState<boolean>(false)

  useEffect(() => {
    if (authLoading || !id) return

    const load = async () => {
      setLoading(true)
      try {
        const snap = await getDoc(doc(db, "healthChecks", id))
        if (!snap.exists()) {
          setErrorMessage("入力対象のレコードが見つかりません。リンクの有効期限が切れているか、URLが正しくない可能性があります。")
          return
        }
        const data = { id: snap.id, ...snap.data() } as HealthCheckData

        if (data.email !== email) {
          setErrorMessage("このチェック表はご本人以外は入力できません。ログイン中のGoogleアカウントが対象従業員のメールアドレスと一致しているかご確認ください。")
          return
        }

        setRecord(data)

        if (data.status === "completed") {
          setSubmitted(true)
          setChecks(data.checks || {})
          setRemarks(data.remarks || "")
        } else {
          const initState: Record<string, string> = {}
          HEALTH_CHECK_ITEMS.forEach(item => {
            initState[item.key] = data.checks?.[item.key] || ""
          })
          setChecks(initState)
        }
      } catch (e) {
        setErrorMessage("データの読み込みに失敗しました: " + JSON.stringify(e))
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [id, email, authLoading])

  const handleCheckChange = (itemKey: string, value: string) => {
    setChecks(prev => ({ ...prev, [itemKey]: value }))
  }

  const isComplete = HEALTH_CHECK_ITEMS.every(item => checks[item.key] === "○" || checks[item.key] === "×")

  const handleSubmit = async () => {
    if (!record?.id) return
    if (!isComplete) {
      toast.error("すべての項目をチェックしてください")
      return
    }

    setProcessing(true)
    try {
      await updateDoc(doc(db, "healthChecks", record.id), {
        checks,
        remarks,
        status: "completed",
        submittedAt: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        updatedAt: dayjs().format("YYYY-MM-DD HH:mm:ss")
      })
      toast.success("健康チェックを送信しました")
      setSubmitted(true)
    } catch (e) {
      toast.error("送信に失敗しました: " + JSON.stringify(e))
    } finally {
      setProcessing(false)
    }
  }

  if (authLoading || loading) {
    return (
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress />
      </Box>
    )
  }

  if (errorMessage) {
    return (
      <Box display="flex" justifyContent="center" mt={10} px={2}>
        <Paper sx={{ p: 3, maxWidth: 480 }}>
          <Typography color="error">{errorMessage}</Typography>
        </Paper>
      </Box>
    )
  }

  if (!record) return null

  return (
    <Box display="flex" justifyContent="center" mt={5} px={2} mb={5}>
      <Paper sx={{ p: 3, maxWidth: 560, width: "100%" }}>
        <Typography variant="h5" fontWeight="bold" mb={1}>
          従業員健康チェック表
        </Typography>
        <Typography variant="body1" mb={1}>
          {dayjs(record.date).format("YYYY年MM月DD日")} {record.employeeName} 様
        </Typography>
        <Typography variant="body2" color="textSecondary" mb={3}>
          原則として作業開始前にご確認ください。異常がある場合は責任者へ報告してください。
        </Typography>

        {submitted ? (
          <Typography color="primary" fontWeight="bold">
            本日の健康チェックは回答済みです。ご協力ありがとうございました。
          </Typography>
        ) : (
          <>
            <TableContainer sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>項目</TableCell>
                    <TableCell align="center">異常なし（○）</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {HEALTH_CHECK_ITEMS.map(item => (
                    <TableRow key={item.key}>
                      <TableCell>{item.label}</TableCell>
                      <TableCell align="center">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={checks[item.key] === "○"}
                              onChange={(e) => handleCheckChange(item.key, e.target.checked ? "○" : "×")}
                              color="success"
                            />
                          }
                          label={checks[item.key] === "×" ? "×" : checks[item.key] === "○" ? "○" : "未回答"}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TextField
              label="特記事項（異常がある場合の措置結果など）"
              multiline
              rows={3}
              fullWidth
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              sx={{ mb: 3 }}
            />

            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleSubmit}
                disabled={processing || !isComplete}
              >
                送信する
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  )
}

export default HealthCheckRespond
